import React, { Component, Fragment } from 'react';
import { PropTypes } from 'prop-types';
import FormInput from '../../Form/FormInput';
import RolesTagInput from '../../Form/RolesTagInput';
import Modal from '../../Modal/Modal';
import getCountryNamesByCode, { getCountryFromCode } from '../../../util/getCountryNamesByCode';

// Check if current deployment is RDL
const isRdl = process.env.APP_NAME === 'rdl';

// Get country list dynamically using i18n-iso-countries (in Spanish, sorted)
const countries = ['', ...getCountryNamesByCode('es')];

// Helper to get display value for country (handles both country codes and names)
const getCountryDisplayValue = (value) => {
  if (!value) return '';
  // If the value is already in the countries list, return it
  if (countries.includes(value)) return value;
  // Otherwise, try to convert from country code to name
  const countryName = getCountryFromCode(value, 'es');
  // If conversion returns a valid country name in our list, use it
  if (countries.includes(countryName)) return countryName;
  // Otherwise return the original value (it might be a custom entry)
  return value;
};

// Tab definitions
const TABS = [
  { id: 'user', label: 'User', editable: true },
  { id: 'address', label: 'Postal Address', editable: true },
  { id: 'history', label: 'History', editable: false },
  { id: 'logins', label: 'Logins', editable: false },
  { id: 'payments', label: 'Payments', editable: false },
  { id: 'account', label: 'Account', editable: false },
];

// Helper to format date - handles various date formats including MongoDB ISODate, timestamps, etc.
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    let date;

    // If it's already a number (timestamp), use it directly
    if (typeof dateStr === 'number') {
      date = new Date(dateStr);
    }
    // If it's a string that looks like a timestamp (all digits)
    else if (typeof dateStr === 'string' && /^\d+$/.test(dateStr)) {
      date = new Date(parseInt(dateStr, 10));
    }
    // If it's an ISO date string or other date format
    else {
      date = new Date(dateStr);
    }

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return dateStr; // Return original if invalid
    }

    return date.toLocaleString();
  } catch (e) {
    return dateStr;
  }
};

class UsersForm extends Component {
  state = {
    canSubmit: true,
    checkedItems: [],
    copySuccess: false,
    activeTab: 'user',
  };

  handleRoles = (checkedItems) => {
    this.setState({ checkedItems });
  };

  setActiveTab = (tabId) => {
    this.setState({ activeTab: tabId });
  };

  copyAddressToClipboard = () => {
    const { formInput } = this.props;
    const {
      addressName,
      addressLastname,
      addressStreet,
      addressStreetNumber,
      addressApartment,
      addressCity,
      addressProvince,
      addressPostalCode,
      addressCountry,
    } = this.state;

    // Use state values if modified, otherwise use formInput values
    const name = addressName !== undefined ? addressName : (formInput?.address?.name || '');
    const lastname = addressLastname !== undefined ? addressLastname : (formInput?.address?.lastname || '');
    const street = addressStreet !== undefined ? addressStreet : (formInput?.address?.street || '');
    const streetNumber = addressStreetNumber !== undefined ? addressStreetNumber : (formInput?.address?.streetNumber || '');
    const apartment = addressApartment !== undefined ? addressApartment : (formInput?.address?.apartment || '');
    const city = addressCity !== undefined ? addressCity : (formInput?.address?.city || '');
    const province = addressProvince !== undefined ? addressProvince : (formInput?.address?.province || '');
    const postalCode = addressPostalCode !== undefined ? addressPostalCode : (formInput?.address?.postalCode || '');
    const country = addressCountry !== undefined ? addressCountry : (formInput?.address?.country || '');

    // Format address as a single string
    const addressParts = [];
    if (name || lastname) addressParts.push(`${name} ${lastname}`.trim());
    if (street || streetNumber) addressParts.push(`${street} ${streetNumber}`.trim());
    if (apartment) addressParts.push(apartment);
    if (city) addressParts.push(city);
    if (province) addressParts.push(province);
    if (postalCode) addressParts.push(postalCode);
    if (country) addressParts.push(country);

    const addressText = addressParts.join(', ');

    if (addressText) {
      navigator.clipboard.writeText(addressText).then(() => {
        this.setState({ copySuccess: true });
        setTimeout(() => this.setState({ copySuccess: false }), 2000);
      }).catch((err) => {
        console.error('Failed to copy address:', err);
      });
    }
  };

  submit = (e) => {
    // Prevent native form submission
    if (e) {
      e.preventDefault();
    }

    const { formInput } = this.props;
    const {
      checkedItems,
      userEmail,
      userName,
      userLastname,
      userIdNumber,
      addressName,
      addressLastname,
      addressStreet,
      addressStreetNumber,
      addressApartment,
      addressCity,
      addressProvince,
      addressPostalCode,
      addressCountry,
    } = this.state;

    const UsersData = {};
    UsersData.email = userEmail || formInput.email;
    UsersData.name = userName !== undefined ? userName : (formInput.name || '');
    UsersData.lastname = userLastname !== undefined ? userLastname : (formInput.lastname || '');
    UsersData.idNumber = userIdNumber !== undefined ? userIdNumber : (formInput.idNumber || '');
    UsersData.roles = checkedItems.length > 0 ? checkedItems : formInput.roles || [];

    // Address fields - pass undefined instead of empty string when not set to avoid overwriting with empty values
    UsersData.addressName = addressName !== undefined ? addressName : formInput.address?.name;
    UsersData.addressLastname = addressLastname !== undefined ? addressLastname : formInput.address?.lastname;
    UsersData.addressStreet = addressStreet !== undefined ? addressStreet : formInput.address?.street;
    UsersData.addressStreetNumber = addressStreetNumber !== undefined ? addressStreetNumber : formInput.address?.streetNumber;
    UsersData.addressApartment = addressApartment !== undefined ? addressApartment : formInput.address?.apartment;
    UsersData.addressCity = addressCity !== undefined ? addressCity : formInput.address?.city;
    UsersData.addressProvince = addressProvince !== undefined ? addressProvince : formInput.address?.province;
    UsersData.addressPostalCode = addressPostalCode !== undefined ? addressPostalCode : formInput.address?.postalCode;
    UsersData.addressCountry = addressCountry !== undefined ? addressCountry : formInput.address?.country;

    console.log('UsersForm submit - UsersData:', UsersData);

    const { retrieveOutput } = this.props;
    retrieveOutput(UsersData);

    const { handleClick, action } = this.props;
    handleClick(action);
  };

  enableButton = () => {
    this.setState({ canSubmit: true });
  };

  disableButton = () => {
    this.setState({ canSubmit: false });
  };

  storeText = (currField, value) => {
    const { allItems } = this.props;
    if (currField === 'venueName') {
      const slugName = value.replace(/ /g, '-').toLowerCase();
      if (allItems.filter(e => e.venueSlugName === slugName).length > 0) {
        console.log('item already exists in the DB');
        this.setState({ slugError: true, canSubmit: false }, () => this.updateSlugName(slugName));
      } else {
        this.setState({ slugError: false, canSubmit: true }, () => this.updateSlugName(slugName));
      }
    }
    this.setState({ [currField]: value }, console.log('name-value', currField, value));
  };

  // Render User tab content
  renderUserTab = () => {
    const { formInput, action } = this.props;
    return (
      <div className="p-2">
        <FormInput name="userEmail" title="Email" validations="isEmail" action={action} storeText={this.storeText} value={(formInput && formInput.email) || ''} ignorePasswordManager autoComplete="off" />
        <div className="grid grid-cols-2 gap-3">
          <FormInput name="userName" title="Name" action={action} storeText={this.storeText} value={(formInput && formInput.name) || ''} ignorePasswordManager autoComplete="off" />
          <FormInput name="userLastname" title="Last Name" action={action} storeText={this.storeText} value={(formInput && formInput.lastname) || ''} ignorePasswordManager autoComplete="off" />
        </div>
        <FormInput name="userIdNumber" title="DNI / ID Number" action={action} storeText={this.storeText} value={(formInput && formInput.idNumber) || ''} ignorePasswordManager autoComplete="off" />
        <div className="w-full mt-1">
          <RolesTagInput
            roles={(this.state.checkedItems.length > 0 ? this.state.checkedItems : (formInput && formInput.roles)) || []}
            onChange={this.handleRoles}
            disabled={action === 'display' || action === 'delete'}
          />
        </div>
      </div>
    );
  };

  // Render Address tab content
  renderAddressTab = () => {
    const { formInput, action } = this.props;
    const { copySuccess } = this.state;

    return (
      <div className="p-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-americaMonoBold text-sm text-gray-300">Postal Address</h3>
          <button
            type="button"
            onClick={this.copyAddressToClipboard}
            className="flex items-center text-xs text-gray-400 hover:text-white cursor-pointer"
            title="Copy address to clipboard"
          >
            {copySuccess ? (
              <span className="text-green-400">✓ Copied!</span>
            ) : (
              <>
                <span className="font-icon icon-stack mr-1" />
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormInput
            name="addressName"
            title="First Name"
            action={action}
            storeText={this.storeText}
            value={(formInput && formInput.address?.name) || ''}
            ignorePasswordManager
            autoComplete="off"
          />
          <FormInput
            name="addressLastname"
            title="Last Name"
            action={action}
            storeText={this.storeText}
            value={(formInput && formInput.address?.lastname) || ''}
            ignorePasswordManager
            autoComplete="off"
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <FormInput
              name="addressStreet"
              title="Street"
              action={action}
              storeText={this.storeText}
              value={(formInput && formInput.address?.street) || ''}
              ignorePasswordManager
              autoComplete="off"
            />
          </div>
          <FormInput
            name="addressStreetNumber"
            title="Number"
            action={action}
            storeText={this.storeText}
            value={(formInput && formInput.address?.streetNumber) || ''}
            ignorePasswordManager
            autoComplete="off"
          />
        </div>
        <FormInput
          name="addressApartment"
          title="Apartment / Floor"
          action={action}
          storeText={this.storeText}
          value={(formInput && formInput.address?.apartment) || ''}
          ignorePasswordManager
          autoComplete="off"
        />
        <div className="grid grid-cols-2 gap-3">
          <FormInput
            name="addressCity"
            title="City"
            action={action}
            storeText={this.storeText}
            value={(formInput && formInput.address?.city) || ''}
            ignorePasswordManager
            autoComplete="off"
          />
          <FormInput
            name="addressProvince"
            title="Province"
            action={action}
            storeText={this.storeText}
            value={(formInput && formInput.address?.province) || ''}
            ignorePasswordManager
            autoComplete="off"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormInput
            name="addressPostalCode"
            title="Postal Code"
            action={action}
            storeText={this.storeText}
            value={(formInput && formInput.address?.postalCode) || ''}
            ignorePasswordManager
            autoComplete="off"
          />
          <div className="w-full">
            <label className="flex flex-col font-americaMonoRegular text-xs uppercase" htmlFor="addressCountry">
              Country
              <select
                id="addressCountry"
                className={`mt-2 rounded-full border-1 p-2 outline-none border-black text-black ${action === 'display' ? 'bg-grey' : 'bg-white'}`}
                onChange={(e) => this.storeText('addressCountry', e.target.value)}
                value={this.state.addressCountry !== undefined ? this.state.addressCountry : getCountryDisplayValue(formInput?.address?.country)}
                disabled={action === 'display'}
                autoComplete="off"
                data-1p-ignore="true"
                data-lpignore="true"
                data-form-type="other"
              >
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country || '-- Select --'}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>
    );
  };

  // Render History tab content (read-only)
  renderHistoryTab = () => {
    const { formInput } = this.props;
    const history = formInput?.history || [];

    if (history.length === 0) {
      return (
        <div className="p-4 text-gray-400 text-center">
          No history records found.
        </div>
      );
    }

    return (
      <div className="p-2 overflow-auto max-h-80">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-600">
              <th className="py-2 px-2">Date</th>
              <th className="py-2 px-2">Type</th>
              <th className="py-2 px-2">Action</th>
              <th className="py-2 px-2">Data</th>
            </tr>
          </thead>
          <tbody>
            {history.map((item, index) => (
              <tr key={index} className="border-b border-gray-700 hover:bg-gray-800">
                <td className="py-2 px-2 whitespace-nowrap">{formatDate(item.date)}</td>
                <td className="py-2 px-2">
                  <span className="px-2 py-1 bg-blue-900 text-blue-200 rounded text-xs">
                    {item.type || '-'}
                  </span>
                </td>
                <td className="py-2 px-2">{item.action || '-'}</td>
                <td className="py-2 px-2">
                  {item.data ? (
                    <details className="cursor-pointer">
                      <summary className="text-gray-400 hover:text-white">View data</summary>
                      <pre className="text-xs mt-2 p-2 bg-gray-900 rounded whitespace-pre-wrap break-words max-w-md">
                        {JSON.stringify(item.data, null, 2)}
                      </pre>
                    </details>
                  ) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render Logins tab content (read-only)
  renderLoginsTab = () => {
    const { formInput } = this.props;
    const logins = formInput?.loggedFrom || [];

    if (logins.length === 0) {
      return (
        <div className="p-4 text-gray-400 text-center">
          No login records found.
        </div>
      );
    }

    // Sort by lastSeen date (most recent first)
    const sortedLogins = [...logins].sort((a, b) => {
      const dateA = new Date(a.lastSeen);
      const dateB = new Date(b.lastSeen);
      return dateB - dateA;
    });

    return (
      <div className="p-2 overflow-auto max-h-80">
        <div className="mb-2 text-xs text-gray-400">
          Total sessions: {logins.length}
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-600">
              <th className="py-2 px-2">Last Seen</th>
              <th className="py-2 px-2">Country</th>
              <th className="py-2 px-2">IP Address</th>
              <th className="py-2 px-2">System</th>
              <th className="py-2 px-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {sortedLogins.map((login, index) => (
              <tr key={index} className="border-b border-gray-700 hover:bg-gray-800">
                <td className="py-2 px-2 whitespace-nowrap">{formatDate(login.lastSeen)}</td>
                <td className="py-2 px-2">
                  <span className="px-2 py-1 bg-gray-700 rounded text-xs">
                    {login.CountryIp || '-'}
                  </span>
                </td>
                <td className="py-2 px-2 font-mono">{login.ip || '-'}</td>
                <td className="py-2 px-2">{login.system || '-'}</td>
                <td className="py-2 px-2">
                  {login.active ? (
                    <span className="px-2 py-1 bg-green-900 text-green-200 rounded text-xs">Active</span>
                  ) : (
                    <span className="px-2 py-1 bg-red-900 text-red-200 rounded text-xs">Inactive</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render Payments tab content (read-only)
  renderPaymentsTab = () => {
    const { formInput } = this.props;
    const paymentMethods = formInput?.paymentMethods || [];

    if (paymentMethods.length === 0) {
      return (
        <div className="p-4 text-gray-400 text-center">
          No payment methods found.
        </div>
      );
    }

    return (
      <div className="p-2 overflow-auto max-h-80">
        <div className="mb-3 text-xs text-gray-400">
          Stripe Customer ID: <span className="font-mono text-white">{formInput?.stripeCustomerId || 'N/A'}</span>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-600">
              <th className="py-2 px-2">Provider</th>
              <th className="py-2 px-2">Card</th>
              <th className="py-2 px-2">Last 4</th>
              <th className="py-2 px-2">Expiry</th>
              <th className="py-2 px-2">Created</th>
              <th className="py-2 px-2">Preferred</th>
            </tr>
          </thead>
          <tbody>
            {paymentMethods.map((method, index) => (
              <tr key={index} className="border-b border-gray-700 hover:bg-gray-800">
                <td className="py-2 px-2">
                  <span className="px-2 py-1 bg-purple-900 text-purple-200 rounded text-xs capitalize">
                    {method.provider || '-'}
                  </span>
                </td>
                <td className="py-2 px-2 capitalize">{method.card?.brand || '-'}</td>
                <td className="py-2 px-2 font-mono">•••• {method.card?.last4 || '----'}</td>
                <td className="py-2 px-2">
                  {method.card?.exp_month && method.card?.exp_year
                    ? `${String(method.card.exp_month).padStart(2, '0')}/${method.card.exp_year}`
                    : '-'}
                </td>
                <td className="py-2 px-2 whitespace-nowrap">{formatDate(method.createdAt)}</td>
                <td className="py-2 px-2">
                  {method.preferred ? (
                    <span className="px-2 py-1 bg-yellow-900 text-yellow-200 rounded text-xs">★ Default</span>
                  ) : (
                    <span className="text-gray-500">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render Account tab content (read-only)
  renderAccountTab = () => {
    const { formInput } = this.props;

    const accountInfo = [
      { label: 'User ID', value: formInput?._id },
      { label: 'Email', value: formInput?.email },
      { label: 'Nickname', value: formInput?.nickname },
      { label: 'Social Network', value: formInput?.socialnetwork },
      { label: 'Server Country', value: formInput?.serverCountry },
      { label: 'Newsletter', value: formInput?.newsletter ? 'Yes' : 'No' },
      { label: 'Newsletter Code', value: formInput?.newsletterCode },
      { label: 'Verified', value: formInput?.verified ? 'Yes' : 'No' },
      { label: 'Active', value: formInput?.active ? 'Yes' : 'No' },
      { label: 'Deleted', value: formInput?.deleted ? 'Yes' : 'No' },
      { label: 'Accepted Conditions', value: formInput?.acceptedConditions ? 'Yes' : 'No' },
      { label: 'Is in Splio', value: formInput?.isInSplio ? 'Yes' : 'No' },
      { label: 'Spotify Linked', value: formInput?.isSpotifyLinked ? 'Yes' : 'No' },
      { label: 'Origin Post', value: formInput?.originPost },
      { label: 'Created At', value: formatDate(formInput?.createdAt) },
      { label: 'Updated At', value: formatDate(formInput?.updatedAt) },
      { label: 'Validation Date', value: formatDate(formInput?.validationDate) },
      { label: 'Validated At', value: formatDate(formInput?.validatedAt) },
    ];

    const rolesData = formInput?.roles || [];
    const favouriteArtists = formInput?.favouriteArtists || [];
    const favoriteConcerts = formInput?.favoriteConcerts || [];
    const social = formInput?.social || [];

    return (
      <div className="p-2 overflow-auto max-h-80">
        {/* Account Info */}
        <div className="mb-4">
          <h4 className="text-sm font-americaMonoBold text-gray-300 mb-2">Account Information</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {accountInfo.map((info, index) => (
              <div key={index} className="flex">
                <span className="text-gray-400 w-32">{info.label}:</span>
                <span className="text-white break-all">{info.value || '-'}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Roles */}
        <div className="mb-4">
          <h4 className="text-sm font-americaMonoBold text-gray-300 mb-2">Roles ({rolesData.length})</h4>
          <div className="flex flex-wrap gap-2">
            {rolesData.length > 0 ? (
              rolesData.map((role, index) => (
                <span key={index} className="px-2 py-1 bg-blue-900 text-blue-200 rounded text-xs">
                  {role}
                </span>
              ))
            ) : (
              <span className="text-gray-400 text-xs">No roles assigned</span>
            )}
          </div>
        </div>

        {/* Favourite Artists */}
        {favouriteArtists.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-americaMonoBold text-gray-300 mb-2">Favourite Artists ({favouriteArtists.length})</h4>
            <div className="flex flex-wrap gap-2">
              {favouriteArtists.map((artist, index) => (
                <span key={index} className="px-2 py-1 bg-green-900 text-green-200 rounded text-xs">
                  {typeof artist === 'object' ? artist.name || JSON.stringify(artist) : artist}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Favorite Concerts */}
        {favoriteConcerts.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-americaMonoBold text-gray-300 mb-2">Favorite Concerts ({favoriteConcerts.length})</h4>
            <div className="flex flex-wrap gap-2">
              {favoriteConcerts.map((concert, index) => (
                <span key={index} className="px-2 py-1 bg-purple-900 text-purple-200 rounded text-xs">
                  {typeof concert === 'object' ? concert.name || JSON.stringify(concert) : concert}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Social Connections */}
        {social.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-americaMonoBold text-gray-300 mb-2">Social Connections ({social.length})</h4>
            <div className="flex flex-wrap gap-2">
              {social.map((item, index) => (
                <span key={index} className="px-2 py-1 bg-orange-900 text-orange-200 rounded text-xs">
                  {typeof item === 'object' ? item.provider || JSON.stringify(item) : item}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render tab content based on active tab
  renderTabContent = () => {
    const { activeTab } = this.state;

    switch (activeTab) {
      case 'user':
        return this.renderUserTab();
      case 'address':
        return this.renderAddressTab();
      case 'history':
        return this.renderHistoryTab();
      case 'logins':
        return this.renderLoginsTab();
      case 'payments':
        return this.renderPaymentsTab();
      case 'account':
        return this.renderAccountTab();
      default:
        return this.renderUserTab();
    }
  };

  render() {
    const { canSubmit, activeTab } = this.state;
    const {
      handleClick, formInput, action, handleDelete, toggleDeleteInfoScreen, canItemBeDeleted,
    } = this.props;

    // Get active tab info for button logic
    const activeTabInfo = TABS.find(tab => tab.id === activeTab);
    const isEditableTab = activeTabInfo?.editable || false;

    return (
      <Fragment>
        <div style={{ backgroundColor: '#000', color: '#fff', marginTop: '40px' }}>
          {/* Tab Navigation */}
          <div style={{ display: 'flex', borderBottom: '1px solid #444', overflowX: 'auto', position: 'relative', zIndex: 100 }}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.setActiveTab(tab.id);
                }}
                style={{
                  padding: '12px 16px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? '2px solid #fff' : '2px solid transparent',
                  backgroundColor: activeTab === tab.id ? '#333' : 'transparent',
                  color: activeTab === tab.id ? '#fff' : '#999',
                  fontStyle: !tab.editable ? 'italic' : 'normal',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.backgroundColor = '#222';
                    e.target.style.color = '#fff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#999';
                  }
                }}
              >
                {tab.label}
                {!tab.editable && <span style={{ marginLeft: '4px', color: '#666' }}>(view)</span>}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <form onSubmit={this.submit} className="login p-4" autoComplete="off" data-1p-ignore="true" data-lpignore="true" data-form-type="other">
            {this.renderTabContent()}

            {/* Action Buttons */}
            <div className="flex justify-end my-2 pt-4 border-t border-gray-700">
              <button type="button" className="font-americaMonoBold text-xs px-6 py-2 rounded-full border-white border-1 bg-black text-white uppercase ml-7 mt-2 w-110px" onClick={() => handleClick(action)} action={action}>
                Cancel
              </button>
              {action !== 'display' && action !== 'delete' && isEditableTab && (
                <button className={`bg-${canSubmit ? 'white' : 'grey'} ${action === 'delete' && 'bg-red text-white'} ${action === 'modify' && 'font-americaMonoBold text-xs px-6 py-2 rounded-full bg-white text-black uppercase ml-7 mt-2 w-110px'} text-black rounded-full py-2 px-4`} type="submit" disabled={!canSubmit}>
                  {`${(action === 'create' && 'Submit') || (action === 'modify' && 'Change') || (action === 'delete' && 'Delete')}`}
                </button>
              )}
              {action === 'delete' && (
                <button
                  type="button"
                  className="bg-red text-white rounded-full py-2 px-4"
                  onClick={() => {
                    handleDelete(formInput.email);
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          </form>
        </div>
        {toggleDeleteInfoScreen ? (
          canItemBeDeleted ? (
            <Modal zIndex="20" title="Item to be deleted" handleClick={handleClick} type="closeDeleteItem">
              {`Item ${formInput.venueName} has been deleted`}
            </Modal>
          ) : (
            <Modal zIndex="20" title="Item cannot be deleted" handleClick={handleClick} type="closeDeleteItem">
              {`Item ${formInput.venueName}, has other records linked to it and it cannot be deleted.`}
            </Modal>
          )
        ) : null}
      </Fragment>
    );
  }
}

UsersForm.propTypes = {
  handleClick: PropTypes.func.isRequired,
  retrieveOutput: PropTypes.func,
  allItems: PropTypes.arrayOf(PropTypes.shape()),
  formInput: PropTypes.shape({}),
  action: PropTypes.string,
  handleDelete: PropTypes.func,
  toggleDeleteInfoScreen: PropTypes.bool,
  canItemBeDeleted: PropTypes.bool,
};

UsersForm.defaultProps = {
  allItems: [],
  formInput: null,
  action: '',
  retrieveOutput: null,
  handleDelete: null,
  toggleDeleteInfoScreen: false,
  canItemBeDeleted: false,
};

export default UsersForm;
